import React, { useCallback, useEffect, useState } from "react";
import "react-quill-new/dist/quill.snow.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiGet } from "../../shared/apiClient";
import { getApiBaseUrl } from "../../config/appSettings";
import { useToast } from "../../shared/ToastContext";

function blogInitials(title) {
  if (!title || !String(title).trim()) return "?";
  const t = String(title).trim();
  return t.length >= 2 ? t.slice(0, 2).toUpperCase() : `${t[0].toUpperCase()}?`;
}

function ArrowLeftIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function EyeIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function RefreshCwIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  );
}

function PencilIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}

const contentProse = [
  "ql-editor max-w-none",
  "text-[1.05rem] leading-[1.75] text-slate-700",
  "[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-slate-900 [&_h1]:mt-8 [&_h1]:mb-3",
  "[&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:mt-6 [&_h2]:mb-2",
  "[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-slate-800 [&_h3]:mt-5 [&_h3]:mb-2",
  "[&_p]:mb-4 [&_p]:leading-[1.8]",
  "[&_a]:text-brand-800 [&_a]:underline decoration-brand-800/30 underline-offset-2 hover:decoration-brand-800",
  "[&_ul]:my-4 [&_ol]:my-4 [&_li]:my-1",
  "[&_blockquote]:border-l-4 [&_blockquote]:border-slate-200 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-600",
  "[&_img]:max-w-full [&_img]:rounded-lg [&_img]:shadow-sm",
  "[&_table]:w-full [&_table]:text-sm"
].join(" ");

export function ViewBlogPage() {
  const { id: idParam } = useParams();
  const id = idParam && /^\d+$/.test(String(idParam)) ? Number(idParam) : NaN;
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);

  const loadPost = useCallback(async () => {
    if (!Number.isFinite(id) || id <= 0) {
      toast.push("Invalid blog", "error");
      navigate("/blogs", { replace: true });
      return;
    }
    setLoading(true);
    const res = await apiGet(`/api/Blogs/${id}`);
    if (!res.ok) {
      toast.push(String(res.error), "error");
      navigate("/blogs", { replace: true });
      setLoading(false);
      return;
    }
    setData(res.data);
    setLoading(false);
  }, [id, navigate, toast]);

  useEffect(() => {
    void loadPost();
  }, [loadPost]);

  const handleRefresh = async () => {
    if (!Number.isFinite(id) || id <= 0) return;
    setRefreshing(true);
    const res = await apiGet(`/api/Blogs/${id}`);
    if (res.ok) {
      setData(res.data);
    } else {
      toast.push(String(res.error), "error");
    }
    setRefreshing(false);
  };

  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }

  if (loading || !data) {
    return (
      <div className="flex w-full min-h-[40vh] items-center justify-center gap-2 text-sm text-slate-500">
        <span
          className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600"
          aria-hidden
        />
        Loading…
      </div>
    );
  }

  const imageSrc =
    data.image && String(data.image).trim()
      ? String(data.image).startsWith("http")
        ? data.image
        : `${getApiBaseUrl()}${data.image}`
      : null;

  return (
    <div className="w-full space-y-4">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">View blog</h1>
            <p className="mt-1 text-sm text-slate-500">Read-only display. Use the actions to edit or return to the list.</p>
          </div>
          <div
            className="flex max-w-full shrink-0 flex-wrap items-center justify-end gap-2"
            role="toolbar"
            aria-label="Post actions"
          >
            <button
              type="button"
              onClick={() => navigate("/blogs")}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
              title="All blogs"
              aria-label="All blogs"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <Link
              to={`/blogs/${id}/edit`}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-brand-700 shadow-sm transition hover:bg-emerald-50 hover:text-brand-800"
              title="Edit"
              aria-label="Edit post"
            >
              <PencilIcon className="h-5 w-5" />
            </Link>
            <button
              type="button"
              onClick={() => void handleRefresh()}
              disabled={refreshing}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-sky-600 shadow-sm transition hover:bg-sky-50 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
              title="Refresh"
              aria-label="Refresh from server"
            >
              {refreshing ? (
                <span
                  className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-sky-200 border-t-sky-600"
                  aria-hidden
                />
              ) : (
                <RefreshCwIcon className="h-5 w-5" />
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate("/blogs")}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-indigo-600 shadow-sm transition hover:bg-indigo-50 hover:text-indigo-700"
              title="Blogs"
              aria-label="Go to blogs list"
            >
              <EyeIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <article className="overflow-hidden rounded-3xl border border-slate-200/60 bg-white shadow-[0_4px_48px_-12px_rgba(15,23,42,0.12),0_0_0_1px_rgba(15,23,42,0.04)]">
        <div className="relative aspect-[2.4/1] min-h-[12rem] max-h-[22rem] w-full overflow-hidden bg-slate-100 sm:aspect-[2.2/1] sm:min-h-[14rem]">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-800 via-brand-800 to-emerald-900">
              <span
                className="select-none rounded-2xl border border-white/20 bg-white/10 px-5 py-2 text-2xl font-bold tracking-[0.2em] text-white/95 shadow-2xl backdrop-blur-sm"
                aria-hidden
              >
                {blogInitials(data.title)}
              </span>
            </div>
          )}
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/20 via-transparent to-slate-900/5"
            aria-hidden
          />
        </div>

        <div className="px-5 py-8 sm:px-8 sm:py-10 md:px-10">
          <header className="border-b border-slate-100/90 pb-8">
            <h1 className="text-balance text-2xl font-bold leading-[1.2] tracking-tight text-slate-900 sm:text-3xl md:text-[2.15rem]">
              {data.title || "—"}
            </h1>
            {data.subtitle && (
              <p className="mt-3 text-pretty text-lg font-normal leading-relaxed text-slate-600 sm:text-xl">
                {data.subtitle}
              </p>
            )}
            {data.shortDescription && (
              <p className="mt-2 text-sm leading-relaxed text-slate-500 sm:text-base">
                {data.shortDescription}
              </p>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500">
              {data.date && (
                <time
                  className="inline-flex items-center gap-2 rounded-full bg-slate-100/90 px-3 py-1 text-slate-600"
                  dateTime={data.date}
                >
                  <span className="text-slate-400" aria-hidden>
                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </span>
                  {new Date(data.date).toLocaleString()}
                </time>
              )}
              {data.authorName && (
                <span className="text-slate-600">
                  <span className="font-medium text-slate-400">By</span> {data.authorName}
                </span>
              )}
            </div>
          </header>

          <div
            className={contentProse + " pt-8"}
            dangerouslySetInnerHTML={{ __html: data.content || "" }}
          />
        </div>
      </article>
    </div>
  );
}
